import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { collection, query, where, onSnapshot, doc, setDoc, updateDoc, deleteField } from "firebase/firestore";
import { useToast } from "../utils/Toast";
import { confirm } from "../utils/confirm";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { useCanSee } from "../hooks/useCanSee";
import PrimoAccessoSezione from "../components/PrimoAccessoSezione";
import { ChevronLeft, ChevronRight, Calendar, Plus, X } from "lucide-react";
import { toLocalDateStr } from "../utils/dateUtils";
import { TIPI_VEICOLO, MEZZO_VUOTO, datiMezzo, conCambioCommessa, chiaveCommessa, eCantiere, autocompilaGiorno, autocompilaSettimana, conflittiMezzi, etichettaMezzo } from "../utils/mezziProgrammazione";
import RicercaMezzoModal from "../components/RicercaMezzoModal";
import { giornoPubblicato, lunediSuccessivo } from "../utils/pubblicazioneProgrammazione";
import { AvvisiSicurezzaProgrammazione } from "../components/SicurezzaCantiere";
import { useNav } from "../contexts/NavContext";
import { linkMappa, descrizioneSede } from "../utils/sediCliente";

function getLunedi(date) {
  const d = new Date(date); const day = d.getDay();
  d.setDate(d.getDate() + (day===0?-6:1-day)); d.setHours(0,0,0,0); return d;
}
function getSettKey(lunedi,orgId) {
  return `${orgId}_${lunedi.getFullYear()}${String(lunedi.getMonth()+1).padStart(2,"0")}${String(lunedi.getDate()).padStart(2,"0")}`;
}
function fmtG(date){return date.toLocaleDateString("it-IT",{weekday:"short",day:"numeric",month:"short"});}
function isSabDom(d){const g=d.getDay();return g===0||g===6;}
function isOggi(date){const t=new Date();t.setHours(0,0,0,0);const d=new Date(date);d.setHours(0,0,0,0);return d.getTime()===t.getTime();}
function getGiorni(lunedi){return Array.from({length:7},(_,i)=>{const d=new Date(lunedi);d.setDate(d.getDate()+i);return d;});}

// ── Genera PDF settimana passata ─────────────────────────────────────────
async function generaPDF(lunedi, giorni, righeVista, celle, celleProssima, orgName, conMezzi = false) {
  const { jsPDF } = await import("jspdf");

  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const fine = new Date(lunedi); fine.setDate(fine.getDate()+6);
  const titolo = `Programmazione Settimanale — ${lunedi.toLocaleDateString("it-IT",{day:"numeric",month:"long",year:"numeric"})} / ${fine.toLocaleDateString("it-IT",{day:"numeric",month:"long",year:"numeric"})}`;

  doc.setFont("helvetica","bold");
  doc.setFontSize(13);
  doc.text(titolo, 14, 14);
  doc.setFontSize(9);
  doc.setFont("helvetica","normal");
  doc.text(orgName||"", 14, 20);

  // Intestazione tabella
  const colW = 32, nameW = 44, startX = 14, startY = 26;
  const giorniLabel = giorni.map(g=>g.toLocaleDateString("it-IT",{weekday:"short",day:"numeric",month:"short"}));

  doc.setFillColor(91,33,212);
  doc.rect(startX, startY, nameW, 7, "F");
  doc.setTextColor(255,255,255);
  doc.setFont("helvetica","bold");
  doc.setFontSize(8);
  doc.text("Dipendente", startX+2, startY+5);
  giorniLabel.forEach((label,i)=>{
    doc.rect(startX+nameW+i*colW, startY, colW, 7, "F");
    doc.text(label.toUpperCase(), startX+nameW+i*colW+2, startY+5);
  });

  // Righe dipendenti
  doc.setTextColor(0,0,0);
  let y = startY+7;
  righeVista.forEach((riga, ri)=>{
    const rowH = 8;
    const bg = ri%2===0 ? [255,255,255] : [249,250,251];
    doc.setFillColor(...bg);
    doc.rect(startX, y, nameW+giorni.length*colW, rowH, "F");
    doc.setFont("helvetica", riga.showName?"bold":"normal");
    doc.setFontSize(8);
    doc.setTextColor(17,24,39);
    if(riga.showName) doc.text(riga.displayName||"", startX+2, y+5.5);

    giorni.forEach((g,gi)=>{
      const ds = toLocalDateStr(g);
      const cella = celle[`${riga.uid}_${riga.rowIndex}_${ds}`]||{};
      doc.setFont("helvetica","normal");
      doc.setFontSize(7.5);
      doc.setTextColor(55,65,81);
      if(cella.commessaNome){
        const txt = cella.commessaNome + (cella.ore?` (${cella.ore}h)`:"");
        doc.text(txt, startX+nameW+gi*colW+2, y+(conMezzi&&cella.mezzoId?3.8:5.5), {maxWidth:colW-4});
      }
      if(conMezzi && cella.mezzoId){
        doc.setFontSize(6.5); doc.setTextColor(91,33,212);
        doc.text(etichettaMezzo(cella), startX+nameW+gi*colW+2, y+7, {maxWidth:colW-4});
        doc.setFontSize(7.5); doc.setTextColor(55,65,81);
      }
    });

    // Bordo riga
    doc.setDrawColor(229,231,235);
    doc.rect(startX, y, nameW+giorni.length*colW, rowH);
    y += rowH;
    if(y > 185) { doc.addPage(); y = 20; }
  });

  const filename = `Programmazione_${toLocalDateStr(lunedi)}.pdf`;
  doc.save(filename);
}

const EXTRA=["Ufficio","Sopralluogo","Commerciale","Magazzino","Ferie","Permesso","Malattia"];
const COLORI={Ferie:"#2563eb",Permesso:"#7c3aed",Malattia:"#dc2626",Magazzino:"#059669",Ufficio:"#0891b2",Sopralluogo:"#f59e0b",Commerciale:"#10b981"};

const PALETTE_RIEMPIMENTO=["#fee2e2","#ffedd5","#fef9c3","#dcfce7","#d1fae5","#cffafe","#dbeafe","#e0e7ff","#ede9fe","#fae8ff","#fce7f3","#f3f4f6","#fecaca","#fed7aa","#fde68a","#bbf7d0","#a7f3d0","#a5f3fc","#bfdbfe","#c7d2fe"];

function CellSelect({commessaNome,commessaId,ore,commesse,commessePreesistenti=[],onCommessaChange,onOreChange,ri,ci,numRighe,numColonne,onTrascinaOreStart,onTrascinaNomeStart,campoEvidenziato,coloreRiempimento,onCambiaColore,mostraOre=true}){
  const [campoAttivo,setCampoAttivo]=useState(null); // "ore" | "nome" | null — quale dei due campi di QUESTA cella ha il focus adesso
  const [nome,setNome]=useState(commessaNome||"");
  const [oreV,setOreV]=useState(ore||"");
  useEffect(()=>setNome(commessaNome||""),[commessaNome]);
  useEffect(()=>setOreV(ore||""),[ore]);
  const col=COLORI[nome];
  const [paletteAperta,setPaletteAperta]=useState(false);
  const timerPressioneRef=useRef(null);
  useEffect(()=>{
    if(!paletteAperta)return;
    const chiudi=()=>setPaletteAperta(false);
    document.addEventListener("mousedown",chiudi);
    return()=>document.removeEventListener("mousedown",chiudi);
  },[paletteAperta]);

  // Navigazione unica su tutto il foglio: le frecce si muovono liberamente tra TUTTI i campi
  // (ore e nome), trattandoli come righe visivamente impilate, senza limiti di direzione.
  const focalizza=(id)=>{
    const target=document.getElementById(id);
    if(target){ target.focus(); target.select&&target.select(); }
  };
  const navigaDaOre=(e)=>{
    if(e.key==="ArrowUp"){ e.preventDefault(); if(ri>0) focalizza(`prognome-${ri-1}-${ci}`); }
    else if(e.key==="ArrowDown"){ e.preventDefault(); focalizza(`prognome-${ri}-${ci}`); }
    else if(e.key==="ArrowLeft"&&e.target.selectionStart===0){ e.preventDefault(); if(ci>0) focalizza(`progore-${ri}-${ci-1}`); }
    else if(e.key==="ArrowRight"&&e.target.selectionStart===e.target.value.length){ e.preventDefault(); if(ci<numColonne-1) focalizza(`progore-${ri}-${ci+1}`); }
  };
  const navigaDaNome=(e)=>{
    if(e.key==="ArrowUp"){
      e.preventDefault();
      // Senza il campo ore visibile, si sale direttamente al "nome" della riga sopra —
      // fluidità richiesta: una sola pressione per riga, non più uno zig-zag ore/nome.
      if(mostraOre) focalizza(`progore-${ri}-${ci}`);
      else if(ri>0) focalizza(`prognome-${ri-1}-${ci}`);
    }
    else if(e.key==="ArrowDown"){
      e.preventDefault();
      if(mostraOre){ if(ri<numRighe-1) focalizza(`progore-${ri+1}-${ci}`); }
      else if(ri<numRighe-1) focalizza(`prognome-${ri+1}-${ci}`);
    }
    else if(e.key==="ArrowLeft"){ e.preventDefault(); if(ci>0) focalizza(`prognome-${ri}-${ci-1}`); }
    else if(e.key==="ArrowRight"){ e.preventDefault(); if(ci<numColonne-1) focalizza(`prognome-${ri}-${ci+1}`); }
  };

  // Tasto destro su tutta la cella apre la palette colore — stesso risultato del quadratino
  // piccolo, bersaglio molto più comodo. Solo se la cella ha già una commessa (come il
  // quadratino, che oggi compare solo in quel caso).
  const apriPaletteDaTastoDestro=(e)=>{
    if(nome==="")return;
    e.preventDefault();
    e.stopPropagation();
    setPaletteAperta(v=>!v);
  };
  // Equivalente per il tocco: tenendo premuto un dito sulla cella per mezzo secondo si apre
  // la stessa palette — sui telefoni e tablet il tasto destro non esiste.
  const iniziaPressioneProlungata=(e)=>{
    if(nome==="")return;
    e.stopPropagation();
    timerPressioneRef.current=setTimeout(()=>setPaletteAperta(true),500);
  };
  const annullaPressioneProlungata=(e)=>{
    e&&e.stopPropagation&&e.stopPropagation();
    if(timerPressioneRef.current){ clearTimeout(timerPressioneRef.current); timerPressioneRef.current=null; }
  };

  return(
    <div style={{display:"flex",flexDirection:"column",gap:1,width:"100%"}}>
      {mostraOre && (
      <div style={{position:"relative",background:(campoEvidenziato==="ore"||campoAttivo==="ore")?"#ede9fe":"transparent",
        outline:(campoEvidenziato==="ore"||campoAttivo==="ore")?"1px dashed #7c3aed":"none",borderRadius:3}}>
        <input id={`progore-${ri}-${ci}`} type="text" inputMode="decimal" value={oreV} autoComplete="off"
          onChange={e=>setOreV(e.target.value)}
          onBlur={e=>{ setCampoAttivo(null); if(e.target.value!==(ore||"")) onOreChange(e.target.value); }}
          onFocus={()=>setCampoAttivo("ore")}
          onKeyDown={navigaDaOre}
          placeholder="h"
          style={{width:"100%",padding:"3px 4px",border:"1px solid transparent",borderRadius:3,
            fontSize:10,fontWeight:700,textAlign:"center",color:"#7c3aed",
            fontFamily:"'Inter',sans-serif",outline:"none",background:"transparent"}}/>
        {oreV!==""&&(
          <span onMouseDown={e=>{e.preventDefault();e.stopPropagation();onTrascinaOreStart&&onTrascinaOreStart(oreV);}}
            title="Trascina per copiare le ore sui giorni successivi"
            style={{position:"absolute",right:0,bottom:0,width:9,height:9,cursor:"grab"}}/>
        )}
      </div>
      )}
      <div style={{position:"relative",
        background:coloreRiempimento||((campoEvidenziato==="nome"||campoAttivo==="nome")?"#ede9fe":"transparent"),
        outline:(campoEvidenziato==="nome"||campoAttivo==="nome")?"1px dashed #7c3aed":"none",borderRadius:3}}
        onContextMenu={apriPaletteDaTastoDestro}
        onTouchStart={iniziaPressioneProlungata}
        onTouchEnd={annullaPressioneProlungata}
        onTouchMove={annullaPressioneProlungata}
        onTouchCancel={annullaPressioneProlungata}>
        <select id={`prognome-${ri}-${ci}`} value={nome}
          onChange={e=>{const v=e.target.value;setNome(v);const f=commesse.find(c=>c.name===v);onCommessaChange(v,f?.id||"");}}
          onFocus={()=>setCampoAttivo("nome")}
          onBlur={()=>setCampoAttivo(null)}
          onKeyDown={navigaDaNome}
          onContextMenu={apriPaletteDaTastoDestro}
          onTouchStart={iniziaPressioneProlungata}
          onTouchEnd={annullaPressioneProlungata}
          onTouchMove={annullaPressioneProlungata}
          onTouchCancel={annullaPressioneProlungata}
          style={{width:"100%",padding:"3px 16px 3px 4px",border:"1px solid transparent",borderRadius:3,
            fontSize:10,fontFamily:"'Inter',sans-serif",outline:"none",
            color:col||"#374151",fontWeight:col?700:400,background:"transparent",minWidth:0}}>
          <option value="">—</option>
          <optgroup label="Commesse Nexiora">
            {commesse.filter(c=>!c.isParent).map(c=><option key={c.id} value={c.name}>{c.name}</option>)}
          </optgroup>
          {commessePreesistenti.length>0&&<optgroup label="Commesse in corso (pregresse)">
            {commessePreesistenti.map(cp=><option key={cp.id} value={cp.nome}>{cp.nome}</option>)}
          </optgroup>}
          <optgroup label="Voci speciali">
            {EXTRA.map(o=><option key={o} value={o}>{o}</option>)}
          </optgroup>
        </select>
        {nome!==""&&(
          <>
            <span onMouseDown={e=>{e.preventDefault();e.stopPropagation();onTrascinaNomeStart&&onTrascinaNomeStart(nome);}}
              title="Trascina per copiare la commessa sui giorni successivi"
              style={{position:"absolute",right:0,bottom:0,width:9,height:9,cursor:"grab"}}/>
            <button type="button" onMouseDown={e=>{e.preventDefault();e.stopPropagation();setPaletteAperta(v=>!v);}}
              title="Colore di riempimento"
              style={{position:"absolute",top:1,right:1,width:9,height:9,padding:0,cursor:"pointer",
                border:"1px solid rgba(0,0,0,.15)",borderRadius:2,
                background:coloreRiempimento||"repeating-conic-gradient(#ddd 0% 25%, #fff 0% 50%) 50%/6px 6px"}}/>
            {paletteAperta&&(
              <div onMouseDown={e=>e.stopPropagation()} onTouchStart={e=>e.stopPropagation()}
                style={{position:"absolute",top:"100%",right:0,zIndex:50,marginTop:2,
                  background:"#fff",border:"1px solid #e5e7eb",borderRadius:8,padding:6,
                  boxShadow:"0 6px 20px rgba(0,0,0,.15)",display:"grid",gridTemplateColumns:"repeat(5,16px)",gap:4}}>
                {PALETTE_RIEMPIMENTO.map(c=>(
                  <div key={c} onMouseDown={e=>{e.preventDefault();e.stopPropagation();onCambiaColore&&onCambiaColore(c);setPaletteAperta(false);}}
                    style={{width:16,height:16,borderRadius:3,background:c,cursor:"pointer",border:"1px solid rgba(0,0,0,.1)"}}/>
                ))}
                <div onMouseDown={e=>{e.preventDefault();e.stopPropagation();onCambiaColore&&onCambiaColore(null);setPaletteAperta(false);}}
                  title="Nessun colore"
                  style={{width:16,height:16,borderRadius:3,cursor:"pointer",border:"1px solid #e5e7eb",
                    display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,color:"#9ca3af",gridColumn:"span 5"}}>
                  ✕ Nessun colore
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function ProgrammazioneSettimanalePage(){
  const { navigateFull } = useNav();
  const {currentUser,isOrgAdmin,isPlatformOwner,userProfile}=useAuth();
  const canSee=useCanSee();
  const {effectiveOrgId}=useOrg();
  const {addToast}=useToast();
  const canEdit=isOrgAdmin||isPlatformOwner||canSee("modifica-programmazione");
  // Automezzi (ott 2026): la riga la vedono chi programma e gli operativi (con la stessa
  // privacy delle righe), non i PM. La ricerca "Chi aveva il mezzo" è per titolare/admin,
  // gestione e amministrazione.
  const vedeMezzi=canEdit||userProfile?.ruoloOperativo==="operativi";
  const puoCercareMezzi=isPlatformOwner||isOrgAdmin||["gestione","amministrazione"].includes(userProfile?.ruoloOperativo);
  const [mezzi,setMezzi]=useState([]);
  const [ultimaAuto,setUltimaAuto]=useState(null); // ultima autocompilazione, per "Annulla" e "Tutta la settimana"
  const [mostraRicercaMezzi,setMostraRicercaMezzi]=useState(false);

  const [lunedi,setLunedi]=useState(()=>getLunedi(new Date()));
  const [utenti,setUtenti]=useState([]);
  const [commesse,setCommesse]=useState([]);
  const [commessePreesistenti,setCommessePreesistenti]=useState([]);
  const [mostraOre,setMostraOre]=useState(true); // finché non arriva la risposta, meglio mostrarlo che nasconderlo per errore
  useEffect(()=>{
    if(!effectiveOrgId) return;
    return onSnapshot(doc(db,"organizations",effectiveOrgId), snap=>{
      setMostraOre(snap.exists() && snap.data().fontOreEconomico==="programmazione");
    });
  },[effectiveOrgId]);
  useEffect(()=>{
    if(!effectiveOrgId) return;
    return onSnapshot(query(collection(db,"commessePreesistenti"),where("orgId","==",effectiveOrgId)),
      snap=>setCommessePreesistenti(snap.docs.map(d=>({id:d.id,...d.data()}))));
  },[effectiveOrgId]);
  const [privacy,setPrivacy]=useState({righeNascoste:[],soloPropriaRiga:false,mostraRigaOwner:false});
  const [coloriCommesse,setColoriCommesse]=useState({});
  const [showPrivacy,setShowPrivacy]=useState(false);
  const [isMobile,setIsMobile]=useState(window.innerWidth<768);

  // Dati
  const [celle,setCelle]=useState({});
  const celleRef=useRef({});
  const [celleProssima,setCelleProssima]=useState({});
  const celleProssimaRef=useRef({});

  // Pubblicazione per giorno — un giorno assente dalla mappa è considerato pubblicato
  // (compatibilità con tutte le settimane già esistenti, create prima di questa funzione:
  // nessuna diventa improvvisamente invisibile). Solo chi può modificare la
  // programmazione decide di smarcare un giorno per tenerci sopra appunti privati finché
  // non è pronto a mostrarlo agli operai.
  const [pubblicati,setPubblicati]=useState({});
  const pubblicatiRef=useRef({});
  const [pubblicatiProssima,setPubblicatiProssima]=useState({});
  const pubblicatiProssimaRef=useRef({});

  // Selezione cella: {uid,rowIndex,dateStr}
  const [sel,setSel]=useState(null);
  const [clipboard,setClipboard]=useState(null);
  const clipboardRef=useRef(null);

  // Coda operazioni — evita race condition su scritture rapide successive
  const operationQueue=useRef(Promise.resolve());
  const enqueue=useCallback(fn=>{
    operationQueue.current=operationQueue.current.then(()=>fn()).catch(()=>fn());
    return operationQueue.current;
  },[]);

  useEffect(()=>{const h=()=>setIsMobile(window.innerWidth<768);window.addEventListener("resize",h);return()=>window.removeEventListener("resize",h);},[]);

  const giorni=useMemo(()=>getGiorni(lunedi),[lunedi]);
  const settKey=useMemo(()=>getSettKey(lunedi,effectiveOrgId),[lunedi,effectiveOrgId]);
  const isSettCorrente=lunedi.getTime()===getLunedi(new Date()).getTime();
  const isSettPassata=lunedi<getLunedi(new Date());
  const oggi=useMemo(()=>{const d=new Date();d.setHours(0,0,0,0);return d;},[]);

  const lunediProssimo=useMemo(()=>{const d=new Date(lunedi);d.setDate(d.getDate()+7);return d;},[lunedi]);
  const giorniProssima=useMemo(()=>getGiorni(lunediProssimo),[lunediProssimo]);
  const settKeyProssima=useMemo(()=>getSettKey(lunediProssimo,effectiveOrgId),[lunediProssimo,effectiveOrgId]);
  const prossLunediStr=useMemo(()=>toLocalDateStr(lunediProssimo),[lunediProssimo]);

  // Sempre 7 giorni Lun-Dom della settimana selezionata, in ordine cronologico naturale —
  // coerente con l'intestazione che mostra sempre l'intervallo lunedì-domenica.
  const giorniFiltrati = giorni;

  useEffect(()=>{if(!effectiveOrgId)return;return onSnapshot(query(collection(db,"users"),where("organizationId","==",effectiveOrgId),where("enabled","==",true)),snap=>{const l=snap.docs.map(d=>d.data());l.sort((a,b)=>(a.displayName||"").localeCompare(b.displayName||"","it"));setUtenti(l);});},[effectiveOrgId]);
  useEffect(()=>{if(!effectiveOrgId)return;return onSnapshot(query(collection(db,"projects"),where("organizationId","==",effectiveOrgId)),snap=>setCommesse(snap.docs.map(d=>({id:d.id,...d.data()})).filter(p=>!p.archived&&!p.isParent)));},[effectiveOrgId]);
  const [privacyPronta,setPrivacyPronta]=useState(false);
  useEffect(()=>{if(!effectiveOrgId)return;setPrivacyPronta(false);return onSnapshot(doc(db,"programmazioniPrivacy",effectiveOrgId),snap=>{if(snap.exists())setPrivacy(snap.data());setPrivacyPronta(true);},()=>setPrivacyPronta(true));},[effectiveOrgId]);
  // "I giorni nuovi partono privati" (ott 2026): la prima volta che chi programma apre la
  // pagina si fissa la decorrenza al lunedì successivo, così la settimana in corso, già
  // vista dagli operativi, non sparisce di colpo.
  useEffect(()=>{
    if(!canEdit||!privacyPronta||!effectiveOrgId)return;
    if(privacy.giorniNuoviPrivati===false||privacy.giorniPrivatiDal)return;
    setDoc(doc(db,"programmazioniPrivacy",effectiveOrgId),{organizationId:effectiveOrgId,giorniPrivatiDal:lunediSuccessivo()},{merge:true})
      .catch(err=>console.warn("Decorrenza giorni privati non salvata:",err));
  },[canEdit,privacyPronta,effectiveOrgId,privacy.giorniNuoviPrivati,privacy.giorniPrivatiDal]);
  useEffect(()=>{if(!effectiveOrgId)return;return onSnapshot(doc(db,"programmazioneColori",effectiveOrgId),snap=>{setColoriCommesse(snap.exists()?(snap.data().colori||{}):{});});},[effectiveOrgId]);
  // Elenco mezzi: solo il Parco mezzi (veicoli), unica anagrafica
  useEffect(()=>{if(!effectiveOrgId||!(vedeMezzi||puoCercareMezzi))return;return onSnapshot(query(collection(db,"mezziAttrezzature"),where("orgId","==",effectiveOrgId)),
    snap=>setMezzi(snap.docs.map(d=>({id:d.id,...d.data()})).filter(m=>TIPI_VEICOLO.includes(m.tipo)).sort((a,b)=>(a.nome||"").localeCompare(b.nome||""))),
    ()=>setMezzi([]));},[effectiveOrgId,vedeMezzi,puoCercareMezzi]);

  useEffect(()=>{if(!effectiveOrgId||!settKey)return;return onSnapshot(doc(db,"programmazioniSettimanali",settKey),snap=>{const d=snap.exists()?snap.data().celle||{}:{};celleRef.current=d;setCelle(d);const p=snap.exists()?snap.data().pubblicati||{}:{};pubblicatiRef.current=p;setPubblicati(p);});},[settKey,effectiveOrgId]);
  useEffect(()=>{if(!effectiveOrgId||!settKeyProssima)return;return onSnapshot(doc(db,"programmazioniSettimanali",settKeyProssima),snap=>{const d=snap.exists()?snap.data().celle||{}:{};celleProssimaRef.current=d;setCelleProssima(d);const p=snap.exists()?snap.data().pubblicati||{}:{};pubblicatiProssimaRef.current=p;setPubblicatiProssima(p);});},[settKeyProssima,effectiveOrgId]);

  const settKeyRef=useRef(settKey);
  const settKeyProssimaRef=useRef(settKeyProssima);
  const orgIdRef=useRef(effectiveOrgId);
  const lunediRef=useRef(lunedi);
  const lunediProssimoRef=useRef(lunediProssimo);
  useEffect(()=>{settKeyRef.current=settKey;},[settKey]);
  useEffect(()=>{settKeyProssimaRef.current=settKeyProssima;},[settKeyProssima]);
  useEffect(()=>{orgIdRef.current=effectiveOrgId;},[effectiveOrgId]);
  useEffect(()=>{lunediRef.current=lunedi;},[lunedi]);
  useEffect(()=>{lunediProssimoRef.current=lunediProssimo;},[lunediProssimo]);

  const salvaCelle=useCallback(async nuove=>{
    celleRef.current=nuove;setCelle({...nuove});
    await setDoc(doc(db,"programmazioniSettimanali",settKeyRef.current),{organizationId:orgIdRef.current,settimana:settKeyRef.current,lunedi:lunediRef.current.toISOString(),celle:nuove},{merge:true});
    addToast("Salvato ✓","success");
  },[addToast]);
  const salvaCelleProssima=useCallback(async nuove=>{
    celleProssimaRef.current=nuove;setCelleProssima({...nuove});
    await setDoc(doc(db,"programmazioniSettimanali",settKeyProssimaRef.current),{organizationId:orgIdRef.current,settimana:settKeyProssimaRef.current,lunedi:lunediProssimoRef.current.toISOString(),celle:nuove},{merge:true});
  },[]);

  const prossLunediStrRef=useRef(prossLunediStr);
  useEffect(()=>{prossLunediStrRef.current=prossLunediStr;},[prossLunediStr]);
  const isProssima=useCallback(dateStr=>dateStr>=prossLunediStrRef.current,[]);

  const isPubblicato=useCallback(dateStr=>{
    const src=isProssima(dateStr)?pubblicatiProssima:pubblicati;
    return giornoPubblicato(src,dateStr,privacy);
  },[pubblicati,pubblicatiProssima,isProssima,privacy]);

  const togglePubblicato=useCallback(async dateStr=>{
    const pross=isProssima(dateStr);
    const mappaAttuale=pross?pubblicatiProssimaRef.current:pubblicatiRef.current;
    // Il quadratino inverte ciò che si vede ora (anche quando il giorno è privato perché
    // "nuovo", senza ancora una scelta salvata).
    const nuovoValore=!giornoPubblicato(mappaAttuale,dateStr,privacy);
    const nuova={...mappaAttuale,[dateStr]:nuovoValore};
    if(pross){
      pubblicatiProssimaRef.current=nuova;setPubblicatiProssima(nuova);
      await setDoc(doc(db,"programmazioniSettimanali",settKeyProssimaRef.current),{organizationId:orgIdRef.current,settimana:settKeyProssimaRef.current,lunedi:lunediProssimoRef.current.toISOString(),pubblicati:nuova},{merge:true});
    } else {
      pubblicatiRef.current=nuova;setPubblicati(nuova);
      await setDoc(doc(db,"programmazioniSettimanali",settKeyRef.current),{organizationId:orgIdRef.current,settimana:settKeyRef.current,lunedi:lunediRef.current.toISOString(),pubblicati:nuova},{merge:true});
    }
  },[isProssima,privacy]);

  const updateCella=useCallback((uid,rowIndex,dateStr,updates)=>{
    enqueue(async()=>{
      const k=`${uid}_${rowIndex}_${dateStr}`;
      if(isProssima(dateStr)){
        const es=celleProssimaRef.current[k]||{};
        const nuove={...celleProssimaRef.current,[k]:{...es,uid,rowIndex,...conCambioCommessa(es,updates)}};
        await salvaCelleProssima(nuove);
      } else {
        const es=celleRef.current[k]||{};
        const nuove={...celleRef.current,[k]:{...es,uid,rowIndex,...conCambioCommessa(es,updates)}};
        await salvaCelle(nuove);
      }
    });
  },[salvaCelle,salvaCelleProssima,isProssima,enqueue]);

  const svuotaCella=useCallback(async(uid,rowIndex,dateStr)=>{
    const k=`celle.${uid}_${rowIndex}_${dateStr}`;
    const chiave=dateStr>=prossLunediStrRef.current?settKeyProssimaRef.current:settKeyRef.current;
    const docRef=doc(db,"programmazioniSettimanali",chiave);
    await updateDoc(docRef,{[k]:deleteField()}).catch(async()=>{
      // Se documento non esiste ancora non fa nulla
    });
    setSel(null);
  },[]);

  const aggiungiRiga=useCallback(async uid=>{
    const keys=Object.keys(celleRef.current).filter(k=>k.startsWith(`${uid}_`));
    const indices=new Set(keys.map(k=>parseInt(k.split("_")[1])).filter(n=>!isNaN(n)));
    let rowIndex=0;while(indices.has(rowIndex))rowIndex++;
    const dateStr=toLocalDateStr(giorni[0]);
    const nuove={...celleRef.current,[`${uid}_${rowIndex}_${dateStr}`]:{uid,rowIndex,commessaNome:"",commessaId:"",ore:""}};
    await salvaCelle(nuove);
  },[salvaCelle,giorni]);

  // Le celle si tolgono dal documento con deleteField: il salvataggio normale (setDoc con
  // merge) unisce le mappe e NON cancella le chiavi mancanti, quindi la riga "eliminata"
  // tornava al ricaricamento successivo.
  const eliminaRiga=useCallback(async(uid,rowIndex)=>{
    const nC={...celleRef.current};
    const nP={...celleProssimaRef.current};
    const togliC={},togliP={};
    Object.keys(nC).forEach(k=>{const p=k.split("_");if(p[0]===uid&&parseInt(p[1])===rowIndex){delete nC[k];togliC[`celle.${k}`]=deleteField();}});
    Object.keys(nP).forEach(k=>{const p=k.split("_");if(p[0]===uid&&parseInt(p[1])===rowIndex){delete nP[k];togliP[`celle.${k}`]=deleteField();}});
    if(Object.keys(togliC).length){
      celleRef.current=nC;setCelle({...nC});
      await updateDoc(doc(db,"programmazioniSettimanali",settKeyRef.current),togliC).catch(err=>addToast("Riga non eliminata: "+(err?.message||""),"error"));
    }
    if(Object.keys(togliP).length){
      celleProssimaRef.current=nP;setCelleProssima({...nP});
      await updateDoc(doc(db,"programmazioniSettimanali",settKeyProssimaRef.current),togliP).catch(err=>addToast("Riga non eliminata: "+(err?.message||""),"error"));
    }
    setSel(null);
  },[addToast]);

  // ── Automezzi ────────────────────────────────────────────────────────────
  // Scrive un insieme di modifiche alle celle della settimana in UN solo salvataggio.
  const scriviMezzi=useCallback(async(modifiche)=>{
    // modifiche: { chiaveCella: datiMezzo }
    const perCorrente={},perProssima={};
    Object.entries(modifiche).forEach(([k,dati])=>{ (isProssima(k.slice(k.lastIndexOf("_")+1))?perProssima:perCorrente)[k]=dati; });
    await enqueue(async()=>{
      if(Object.keys(perCorrente).length){
        const nuove={...celleRef.current};
        Object.entries(perCorrente).forEach(([k,dati])=>{ const p=k.split("_"); nuove[k]={...(nuove[k]||{}),uid:p[0],rowIndex:parseInt(p[1]),...dati}; });
        await salvaCelle(nuove);
      }
      if(Object.keys(perProssima).length){
        const nuove={...celleProssimaRef.current};
        Object.entries(perProssima).forEach(([k,dati])=>{ const p=k.split("_"); nuove[k]={...(nuove[k]||{}),uid:p[0],rowIndex:parseInt(p[1]),...dati}; });
        await salvaCelleProssima(nuove);
      }
    });
  },[enqueue,salvaCelle,salvaCelleProssima,isProssima]);

  const nomeDiChiave=useCallback(k=>utenti.find(u=>u.uid===k.split("_")[0])?.displayName||"",[utenti]);

  // Scelta del mezzo in una cella: manuale su questa, automatica sui colleghi della stessa
  // commessa quel giorno che non ne hanno ancora uno.
  const assegnaMezzo=useCallback(async(uid,rowIndex,dateStr,mezzoId)=>{
    const k=`${uid}_${rowIndex}_${dateStr}`;
    const mappa=isProssima(dateStr)?celleProssimaRef.current:celleRef.current;
    const cella=mappa[k]||{};
    const mezzo=mezzi.find(m=>m.id===mezzoId)||null;
    const modifiche={[k]:mezzo?datiMezzo(mezzo,false):{...MEZZO_VUOTO}};
    let auto=[];
    if(mezzo&&eCantiere(cella)){
      auto=autocompilaGiorno(mappa,k,dateStr,chiaveCommessa(cella));
      auto.forEach(x=>{modifiche[x]=datiMezzo(mezzo,true);});
    }
    await scriviMezzi(modifiche);
    if(mezzo&&eCantiere(cella)){
      setUltimaAuto({mezzo,uid,rowIndex,dateStr,commessaKey:chiaveCommessa(cella),commessaNome:cella.commessaNome,chiavi:auto,
        nomi:auto.map(nomeDiChiave).filter(Boolean)});
    }else setUltimaAuto(null);
  },[mezzi,isProssima,scriviMezzi,nomeDiChiave]);

  const annullaAutocompilazione=useCallback(async()=>{
    if(!ultimaAuto)return;
    const modifiche={};
    ultimaAuto.chiavi.forEach(k=>{
      const c=(isProssima(k.slice(k.lastIndexOf("_")+1))?celleProssimaRef.current:celleRef.current)[k];
      if(c?.mezzoAuto&&c.mezzoId===ultimaAuto.mezzo.id) modifiche[k]={...MEZZO_VUOTO}; // solo ciò che è ancora com'era
    });
    if(Object.keys(modifiche).length) await scriviMezzi(modifiche);
    addToast("Autocompilazione annullata","info");
    setUltimaAuto(null);
  },[ultimaAuto,isProssima,scriviMezzi,addToast]);

  const applicaTuttaSettimana=useCallback(async()=>{
    if(!ultimaAuto)return;
    const giorniSett=giorni.map(g=>toLocalDateStr(g));
    const mappa=isProssima(ultimaAuto.dateStr)?celleProssimaRef.current:celleRef.current;
    const chiavi=autocompilaSettimana(mappa,{uid:ultimaAuto.uid,rowIndex:ultimaAuto.rowIndex,dateStr:ultimaAuto.dateStr,giorniSettimana:giorniSett,commessaKey:ultimaAuto.commessaKey});
    if(!chiavi.length){ addToast("Nessun altro giorno da compilare: negli altri giorni il mezzo c'è già o la squadra è su un altro cantiere","info"); return; }
    const modifiche={}; chiavi.forEach(k=>{modifiche[k]=datiMezzo(ultimaAuto.mezzo,true);});
    await scriviMezzi(modifiche);
    addToast(`${etichettaMezzo(ultimaAuto.mezzo)} assegnato anche a ${chiavi.length} ${chiavi.length===1?"giornata":"giornate"} della settimana`,"success");
    setUltimaAuto(u=>u?{...u,chiavi:[...u.chiavi,...chiavi],settimanaApplicata:true}:u);
  },[ultimaAuto,giorni,isProssima,scriviMezzi,addToast]);

  const conflitti=useMemo(()=>conflittiMezzi({...celle,...celleProssima}),[celle,celleProssima]);

  const getCella=useCallback((uid,rowIndex,dateStr)=>{
    const src=isProssima(dateStr)?celleProssima:celle;
    return src[`${uid}_${rowIndex}_${dateStr}`]||{};
  },[celle,celleProssima,isProssima]);

  const utentiFiltrati=useMemo(()=>utenti.filter(u=>{
    if(isPlatformOwner||isOrgAdmin)return true;
    if(u.uid===currentUser?.uid)return true;
    const isOwner=privacy.ownerUid===u.uid||u.role==="owner"||u.isPlatformOwner;
    if(isOwner&&!privacy.mostraRigaOwner)return false;
    if(privacy.righeNascoste?.includes(u.uid))return false;
    if(privacy.soloPropriaRiga&&u.uid!==currentUser?.uid)return false;
    return true;
  }),[utenti,privacy,isPlatformOwner,isOrgAdmin,currentUser]);

  const righeVista=useMemo(()=>{
    const result=[];
    utentiFiltrati.forEach(u=>{
      const tutteLeCelle={...celleRef.current,...celleProssimaRef.current};
      const keys=Object.keys(tutteLeCelle).filter(k=>k.startsWith(`${u.uid}_`));
      const indices=[...new Set(keys.map(k=>parseInt(k.split("_")[1])).filter(n=>!isNaN(n)))].sort((a,b)=>a-b);
      const righeU=indices.length>0?indices:[0];
      righeU.forEach((rowIndex,ri)=>{
        result.push({uid:u.uid,displayName:u.displayName,showName:ri===0,rowIndex,isLastOfUser:ri===righeU.length-1});
      });
    });
    return result;
  },[utentiFiltrati,celle,celleProssima]);


// Fill handle
  // Trascinamento orizzontale — un solo tipo di valore alla volta (ore OPPURE nome),
  // sempre sulla stessa riga (stesso operaio), giorno dopo giorno. Aggiorna lo stato locale
  // mentre trascini (feedback immediato) e scrive su Firestore UNA SOLA VOLTA al rilascio,
  // invece che una volta per ogni cella attraversata.
  const [trascinamento,setTrascinamento]=useState(null); // {tipo:'ore'|'nome', ri, ci, dateStr, valore, commessaId}
  const [trascinaSuCi,setTrascinaSuCi]=useState(null);
  const toccateRef=useRef({corrente:false,prossima:false});

  const applicaLocale=useCallback((uid,rowIndex,dateStr,updates)=>{
    const k=`${uid}_${rowIndex}_${dateStr}`;
    if(isProssima(dateStr)){
      const es=celleProssimaRef.current[k]||{};
      celleProssimaRef.current={...celleProssimaRef.current,[k]:{...es,uid,rowIndex,...conCambioCommessa(es,updates)}};
      setCelleProssima({...celleProssimaRef.current});
      toccateRef.current.prossima=true;
    }else{
      const es=celleRef.current[k]||{};
      celleRef.current={...celleRef.current,[k]:{...es,uid,rowIndex,...conCambioCommessa(es,updates)}};
      setCelle({...celleRef.current});
      toccateRef.current.corrente=true;
    }
  },[isProssima]);

  const flushCelle=useCallback(async()=>{
    const {corrente,prossima}=toccateRef.current;
    if(!corrente&&!prossima)return;
    await enqueue(async()=>{
      if(corrente){
        await setDoc(doc(db,"programmazioniSettimanali",settKeyRef.current),{organizationId:orgIdRef.current,settimana:settKeyRef.current,lunedi:lunediRef.current.toISOString(),celle:celleRef.current},{merge:true});
      }
      if(prossima){
        await setDoc(doc(db,"programmazioniSettimanali",settKeyProssimaRef.current),{organizationId:orgIdRef.current,settimana:settKeyProssimaRef.current,lunedi:lunediProssimoRef.current.toISOString(),celle:celleProssimaRef.current},{merge:true});
      }
      addToast("Salvato ✓","success");
    });
    toccateRef.current={corrente:false,prossima:false};
  },[enqueue,addToast]);

  useEffect(()=>{
    if(!trascinamento)return;
    let ultimaCi=trascinamento.ci;
    const trovaColonnaSottoCursore=(clientX,clientY)=>{
      const el=document.elementFromPoint(clientX,clientY);
      const td=el&&el.closest?el.closest("td[data-ri]"):null;
      if(!td)return null;
      const ri=parseInt(td.getAttribute("data-ri")),ci=parseInt(td.getAttribute("data-ci"));
      if(isNaN(ri)||isNaN(ci))return null;
      if(ri!==trascinamento.ri)return null; // solo stessa riga: trascinamento orizzontale
      return ci;
    };
    const applicaSuGiorno=(ci)=>{
      const rigaTarget=righeVista[trascinamento.ri],giornoTarget=giorniFiltrati[ci];
      if(!rigaTarget||!giornoTarget)return;
      const dsTarget=toLocalDateStr(giornoTarget);
      if(dsTarget===trascinamento.dateStr)return;
      if(trascinamento.tipo==="ore"){
        applicaLocale(rigaTarget.uid,rigaTarget.rowIndex,dsTarget,{ore:trascinamento.valore});
      }else{
        applicaLocale(rigaTarget.uid,rigaTarget.rowIndex,dsTarget,{commessaNome:trascinamento.valore,commessaId:trascinamento.commessaId||""});
      }
    };
    const onMove=e=>{
      const ci=trovaColonnaSottoCursore(e.clientX,e.clientY);
      if(ci===null)return;
      setTrascinaSuCi(ci);
      if(ci!==ultimaCi){
        const passo=ci>ultimaCi?1:-1;
        for(let c=ultimaCi+passo; passo>0?c<=ci:c>=ci; c+=passo) applicaSuGiorno(c);
        ultimaCi=ci;
      }
    };
    const onUp=()=>{
      setTrascinamento(null);
      setTrascinaSuCi(null);
      document.body.style.cursor="";document.body.style.userSelect="";
      flushCelle();
    };
    document.body.style.cursor="grabbing";document.body.style.userSelect="none";
    window.addEventListener("mousemove",onMove);window.addEventListener("mouseup",onUp);
    return()=>{window.removeEventListener("mousemove",onMove);window.removeEventListener("mouseup",onUp);};
  },[trascinamento,righeVista,giorniFiltrati,applicaLocale,flushCelle]);


  // Tastiera
  useEffect(()=>{
    if(!canEdit||isSettPassata)return;
    const h=async e=>{
      if(e.target.tagName==="INPUT"||e.target.tagName==="SELECT")return;
      if((e.ctrlKey||e.metaKey)&&e.key==="c"&&sel){
        const v=getCella(sel.uid,sel.rowIndex,sel.dateStr);
        if(v.commessaNome){const val={...v};setClipboard(val);clipboardRef.current=val;}
        e.preventDefault();return;
      }
      if((e.ctrlKey||e.metaKey)&&e.key==="v"&&clipboardRef.current&&sel){
        const cb=clipboardRef.current;
        await updateCella(sel.uid,sel.rowIndex,sel.dateStr,{commessaNome:cb.commessaNome||"",commessaId:cb.commessaId||"",ore:cb.ore||""});
        e.preventDefault();return;
      }
      if((e.key==="Delete"||e.key==="Backspace")&&sel){
        await svuotaCella(sel.uid,sel.rowIndex,sel.dateStr);
        e.preventDefault();
      }
      if(e.key==="Escape")setSel(null);
    };
    window.addEventListener("keydown",h);return()=>window.removeEventListener("keydown",h);
  },[canEdit,isSettPassata,sel,getCella,updateCella,svuotaCella]);

  // Deselezione click fuori — ma NON se si clicca la X o un pulsante della griglia
  useEffect(()=>{
    const h=e=>{
      if(e.target.closest("[data-fill]"))return;
      if(e.target.closest("[data-cell-btn]"))return;
      setSel(null);
    };
    document.addEventListener("mousedown",h);return()=>document.removeEventListener("mousedown",h);
  },[]);

  const salvaPrivacy=async nuova=>{await setDoc(doc(db,"programmazioniPrivacy",effectiveOrgId),{...nuova,organizationId:effectiveOrgId,ownerUid:nuova.ownerUid||currentUser?.uid},{merge:true});setPrivacy(nuova);};
  const salvaColoreCommessa=useCallback(async(chiave,colore)=>{
    const nuovi={...coloriCommesse};
    if(colore) nuovi[chiave]=colore; else delete nuovi[chiave];
    setColoriCommesse(nuovi); // ottimistico, per la stessa reattività di Excel
    await setDoc(doc(db,"programmazioneColori",effectiveOrgId),{organizationId:effectiveOrgId,colori:nuovi},{merge:true});
  },[coloriCommesse,effectiveOrgId]);


  // Mobile
  if(isMobile){
    const oggi2=new Date();oggi2.setHours(0,0,0,0);
    const domani=new Date(oggi2);domani.setDate(domani.getDate()+1);
    // Su mobile: usa righeVista che già rispetta le impostazioni privacy del titolare
    const mieRighe = righeVista;
    // Giorni: da oggi in poi (incluso oggi)
    const giorniMobile=giorniFiltrati.filter(g=>{const d=new Date(g);d.setHours(0,0,0,0);return d>=oggi2;});
    const isDomani=(g)=>{const d=new Date(g);d.setHours(0,0,0,0);return d.getTime()===domani.getTime();};

    return(
      <div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden"}}>
        {/* Header mobile */}
        <div style={{background:"#fff",borderBottom:"1px solid #e5e7eb",padding:"10px 16px",flexShrink:0}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
            <h2 style={{fontSize:15,fontWeight:700,color:"#7c3aed",margin:0}}>📅 La mia settimana</h2>
            <div style={{display:"flex",gap:6}}>
              <button onClick={()=>setLunedi(d=>{const n=new Date(d);n.setDate(n.getDate()-7);return n;})}
                style={{border:"1px solid #e5e7eb",background:"#fff",borderRadius:6,padding:"4px 8px",cursor:"pointer",fontSize:12}}>‹</button>
              <button onClick={()=>setLunedi(getLunedi(new Date()))}
                style={{border:"1px solid #7c3aed",background:"#ede9fe",color:"#7c3aed",borderRadius:6,padding:"4px 8px",cursor:"pointer",fontSize:12,fontWeight:600}}>Oggi</button>
              <button onClick={()=>setLunedi(d=>{const n=new Date(d);n.setDate(n.getDate()+7);return n;})}
                style={{border:"1px solid #e5e7eb",background:"#fff",borderRadius:6,padding:"4px 8px",cursor:"pointer",fontSize:12}}>›</button>
            </div>
          </div>
        </div>
        {/* Lista giorni mobile */}
        <div style={{flex:1,overflowY:"auto",padding:12}}>
          {giorniMobile.map(g=>{
            const dateStr=toLocalDateStr(g);
            const ogg=isOggi(g),dom=isDomani(g),sab=isSabDom(g);
            // Un giorno non ancora pubblicato non si vede da telefono, come su computer
            // (prima la vista da telefono lo mostrava comunque agli operai).
            const nascosto=!canEdit&&!isPubblicato(dateStr);
            const voci=nascosto?[]:mieRighe.flatMap(r=>{
              const cella=getCella(r.uid,r.rowIndex,dateStr);
              if(!cella.commessaNome) return [];
              return [{nome:r.displayName,commessa:cella.commessaNome,ore:cella.ore,tipo:cella.tipo,mezzo:vedeMezzi&&cella.mezzoId?etichettaMezzo(cella):""}];
            });
            return(
              <div key={dateStr} style={{
                background:dom?"#faf5ff":ogg?"#f0fdf4":sab?"#fff5f5":"#fff",
                border:`1px solid ${dom?"#c4b5fd":ogg?"#6ee7b7":sab?"#fca5a5":"#e5e7eb"}`,
                borderLeft:`4px solid ${dom?"#7c3aed":ogg?"#059669":sab?"#fca5a5":"#e5e7eb"}`,
                borderRadius:10,padding:12,marginBottom:8
              }}>
                <div style={{fontSize:11,fontWeight:800,color:dom?"#7c3aed":ogg?"#059669":sab?"#dc2626":"#9ca3af",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:6}}>
                  {dom&&"➡️ "}{ogg&&"📍 "}
                  {g.toLocaleDateString("it-IT",{weekday:"long",day:"numeric",month:"long"})}
                  {dom&&" — DOMANI"}{ogg&&" — OGGI"}
                </div>
                {voci.length>0?(
                  <div>
                    {voci.map((v,i)=>(
                      <div key={i} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"5px 0",borderBottom:i<voci.length-1?"1px solid #f3f4f6":"none"}}>
                        <div>
                          {(isOrgAdmin||isPlatformOwner)&&<div style={{fontSize:10,color:"#9ca3af",fontWeight:600}}>{v.nome}</div>}
                          <div style={{fontSize:13,fontWeight:600,color:"#111827"}}>{v.commessa}</div>
                          {v.mezzo&&<div style={{fontSize:11.5,color:"#6d28d9",fontWeight:600,marginTop:2}}>🚐 {v.mezzo}</div>}
                        </div>
                        {v.ore&&<span style={{background:"#ede9fe",color:"#7c3aed",borderRadius:8,padding:"2px 10px",fontSize:12,fontWeight:700}}>{v.ore}h</span>}
                      </div>
                    ))}
                  </div>
                ):(
                  <div style={{fontSize:12,color:"#d1d5db",fontStyle:"italic"}}>Nessuna attività programmata</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  const COL_DIP=190,COL_G=200;
  const tabW=COL_DIP+giorniFiltrati.length*COL_G;

  return(
    <div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden"}}>

      {/* Header */}
      <div style={{background:"#fff",borderBottom:"1px solid #e5e7eb",padding:"12px 20px",flexShrink:0}}>
        <div>
          <h2 style={{fontSize:17,fontWeight:700,display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
            <Calendar size={18} color="#7c3aed"/> Programmazione Settimanale
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <button onClick={()=>setLunedi(d=>{const n=new Date(d);n.setDate(n.getDate()-7);return n;})}
              style={{border:"1px solid #e5e7eb",background:"#fff",borderRadius:7,padding:"5px 10px",cursor:"pointer",display:"flex",alignItems:"center"}}>
              <ChevronLeft size={16}/>
            </button>
            <div style={{fontSize:13,fontWeight:700,minWidth:200,textAlign:"center"}}>
              {isSettCorrente&&<span style={{color:"#7c3aed",marginRight:6}}>●</span>}
              {lunedi.toLocaleDateString("it-IT",{day:"numeric",month:"long"})} — {giorni[6].toLocaleDateString("it-IT",{day:"numeric",month:"long",year:"numeric"})}
            </div>
            <button onClick={()=>setLunedi(d=>{const n=new Date(d);n.setDate(n.getDate()+7);return n;})}
              style={{border:"1px solid #e5e7eb",background:"#fff",borderRadius:7,padding:"5px 10px",cursor:"pointer",display:"flex",alignItems:"center"}}>
              <ChevronRight size={16}/>
            </button>
            {isSettPassata&&(
              <button
                onClick={()=>generaPDF(lunedi,giorni,righeVista,celle,celleProssima,effectiveOrgId,vedeMezzi)}
                style={{border:"1px solid #dc2626",background:"#fee2e2",color:"#dc2626",borderRadius:7,padding:"5px 12px",cursor:"pointer",fontSize:11,fontWeight:600,fontFamily:"'Inter',sans-serif",display:"flex",alignItems:"center",gap:4}}>
                📄 Esporta PDF
              </button>
            )}
            {!isSettCorrente&&<button onClick={()=>setLunedi(getLunedi(new Date()))}
              style={{border:"1px solid #7c3aed",background:"#ede9fe",color:"#7c3aed",borderRadius:7,padding:"5px 12px",cursor:"pointer",fontSize:12,fontWeight:600,fontFamily:"'Inter',sans-serif"}}>
              Oggi
            </button>}
            {(isPlatformOwner||isOrgAdmin)&&<button onClick={()=>setShowPrivacy(!showPrivacy)}
              style={{border:"1px solid #e5e7eb",background:showPrivacy?"#f3f4f6":"#fff",borderRadius:7,padding:"5px 12px",cursor:"pointer",fontSize:12,fontFamily:"'Inter',sans-serif",color:"#6b7280"}}>
              🔒 Privacy
            </button>}
            {puoCercareMezzi&&<button onClick={()=>setMostraRicercaMezzi(true)}
              title="Chi aveva un mezzo in una certa data (per i verbali)"
              style={{border:"1px solid #ddd6fe",background:"#f5f3ff",borderRadius:7,padding:"5px 12px",cursor:"pointer",fontSize:12,fontFamily:"'Inter',sans-serif",color:"#6d28d9",fontWeight:600}}>
              🔍 Chi aveva il mezzo
            </button>}
            {(isPlatformOwner||isOrgAdmin)&&(
              <button onClick={async()=>{
                // Duplica programmazione settimana corrente nella prossima
                if(!await confirm(
                  "Copiare la programmazione di questa settimana nella settimana successiva?\n\nLe celle già compilate nella settimana successiva verranno sovrascritte.",
                  {title:"Duplica programmazione",type:"warning",confirmLabel:"Sì, duplica"}
                )) return;
                // Calcola il lunedì prossimo
                const lunProssimo = new Date(lunedi); lunProssimo.setDate(lunProssimo.getDate()+7);
                const nuoveCelle = {};
                Object.entries(celle).forEach(([k,v])=>{
                  // La chiave è uid_rowIndex_yyyy-mm-dd — sostituisci la data
                  const parts = k.split("_");
                  if(parts.length<3) return;
                  const dateStr = parts.slice(2).join("_");
                  const oldDate = new Date(dateStr+"T00:00:00");
                  const diff = oldDate.getDay()===0?6:oldDate.getDay()-1; // giorni dal lunedì
                  const newDate = new Date(lunProssimo); newDate.setDate(newDate.getDate()+diff);
                  const newDateStr = toLocalDateStr(newDate);
                  const newKey = parts.slice(0,2).join("_")+"_"+newDateStr;
                  nuoveCelle[newKey] = {...v};
                });
                const nuovaKey = getSettKey(lunProssimo, effectiveOrgId);
                await setDoc(doc(db,"programmazioniSettimanali",nuovaKey),{
                  organizationId:effectiveOrgId, settimana:nuovaKey,
                  lunedi:lunProssimo.toISOString(), celle:nuoveCelle
                },{merge:false});
                addToast("✅ Programmazione duplicata nella settimana successiva","success");
              }}
              style={{border:"1px solid #7c3aed",background:"#ede9fe",color:"#7c3aed",borderRadius:7,padding:"5px 12px",cursor:"pointer",fontSize:12,fontWeight:600,fontFamily:"'Inter',sans-serif",whiteSpace:"nowrap"}}>
                ⧉ Duplica settimana
              </button>
            )}
          </div>
          </h2>
        </div>
        <div style={{display:"flex",gap:12,alignItems:"center",marginTop:4,fontSize:11,flexWrap:"wrap"}}>
          {isSettPassata&&<span style={{color:"#9ca3af"}}>📁 Settimana passata — sola lettura</span>}
          {clipboard&&<span style={{color:"#7c3aed"}}>📋 Ctrl+V per incollare</span>}
          {canEdit&&!isSettPassata&&<span style={{marginLeft:"auto",color:"#9ca3af"}}>{mostraOre?"Ore sopra, commessa sotto · ":""}Trascina il puntino di un valore per copiarlo sui giorni successivi (stessa riga) · ✕ cancella · Ctrl+C/V · Frecce = spostati in ogni direzione tra tutti i campi</span>}
        </div>
      </div>

      <div style={{padding:"12px 20px 0",flexShrink:0}}>
        <PrimoAccessoSezione sezioneId="programmazione"/>
      </div>

      {mostraRicercaMezzi&&(
        <RicercaMezzoModal orgId={effectiveOrgId} utenti={utenti} mezzi={mezzi} addToast={addToast} onClose={()=>setMostraRicercaMezzi(false)}/>
      )}
      {/* Esito dell'ultima scelta di mezzo: cosa è stato compilato in automatico, con Annulla */}
      {ultimaAuto&&canEdit&&(
        <div style={{background:"#f5f3ff",borderBottom:"1px solid #ddd6fe",padding:"7px 20px",flexShrink:0,display:"flex",alignItems:"center",gap:10,flexWrap:"wrap",fontSize:12,color:"#4c1d95"}}>
          <span style={{flex:1,minWidth:220}}>
            🚐 <strong>{etichettaMezzo(ultimaAuto.mezzo)}</strong> sulla {ultimaAuto.commessaNome}
            {ultimaAuto.nomi.length>0?<> — assegnato in automatico anche a {ultimaAuto.nomi.join(", ")}</>:" — nessun collega senza mezzo su questo cantiere oggi"}
            {ultimaAuto.settimanaApplicata&&" · esteso alla settimana"}
          </span>
          {!ultimaAuto.settimanaApplicata&&<button onClick={applicaTuttaSettimana} style={{fontSize:11.5,fontWeight:700,border:"1px solid #7c3aed",background:"#7c3aed",color:"#fff",borderRadius:6,padding:"4px 10px",cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>Applica a tutta la settimana</button>}
          {ultimaAuto.chiavi.length>0&&<button onClick={annullaAutocompilazione} style={{fontSize:11.5,border:"1px solid #c4b5fd",background:"#fff",color:"#6d28d9",borderRadius:6,padding:"4px 10px",cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>Annulla autocompilazione</button>}
          <button onClick={()=>setUltimaAuto(null)} title="Chiudi" style={{border:"none",background:"none",cursor:"pointer",color:"#a78bfa",fontSize:14}}>✕</button>
        </div>
      )}
      {/* Privacy */}
      {showPrivacy&&(isPlatformOwner||isOrgAdmin)&&(
        <div style={{background:"#fff",borderBottom:"1px solid #e5e7eb",padding:"10px 20px",flexShrink:0}}>
          <div style={{display:"flex",gap:20,flexWrap:"wrap"}}>
            <label style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontSize:12}}>
              <input type="checkbox" checked={!!privacy.mostraRigaOwner} onChange={e=>salvaPrivacy({...privacy,mostraRigaOwner:e.target.checked})} style={{accentColor:"#7c3aed"}}/>
              Mostra mia riga a tutti
            </label>
            <label style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontSize:12}}>
              <input type="checkbox" checked={!!privacy.soloPropriaRiga} onChange={e=>salvaPrivacy({...privacy,soloPropriaRiga:e.target.checked})} style={{accentColor:"#7c3aed"}}/>
              Ogni dipendente vede solo la propria riga
            </label>
            <label style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontSize:12}}
              title="Un giorno nuovo resta nascosto a chi non programma finché non spunti il quadratino in testa alla colonna">
              <input type="checkbox" checked={privacy.giorniNuoviPrivati!==false}
                onChange={e=>salvaPrivacy({...privacy,giorniNuoviPrivati:e.target.checked,
                  giorniPrivatiDal:e.target.checked?(privacy.giorniPrivatiDal||lunediSuccessivo()):privacy.giorniPrivatiDal})}
                style={{accentColor:"#7c3aed"}}/>
              I giorni nuovi partono privati
              {privacy.giorniNuoviPrivati!==false&&privacy.giorniPrivatiDal&&(
                <span style={{color:"#9ca3af"}}>(dal {new Date(privacy.giorniPrivatiDal+"T00:00:00").toLocaleDateString("it-IT")})</span>
              )}
            </label>
          </div>
        </div>
      )}

      {/* Sicurezza di cantiere: cantieri programmati con adempimenti aperti (avviso non bloccante) */}
      <AvvisiSicurezzaProgrammazione orgId={effectiveOrgId} commesse={commesse}
        idInProgramma={Object.values(celle).map(c=>c?.commessaId)}
        onApri={id=>navigateFull({page:"project",projectId:id,initialTab:"sicurezza"})}/>

      {/* Griglia */}
      <div style={{flex:1,overflowY:"auto",overflowX:"scroll"}}>
        <table style={{borderCollapse:"collapse",tableLayout:"fixed",width:tabW,minWidth:tabW}}>
          <colgroup>
            <col style={{width:COL_DIP}}/>
            {giorniFiltrati.map((_,i)=><col key={i} style={{width:COL_G}}/>)}
          </colgroup>
          <thead style={{position:"sticky",top:0,zIndex:10}}>
            <tr style={{background:"#f9fafb",borderBottom:"2px solid #e5e7eb"}}>
              <th style={{padding:"10px 14px",textAlign:"left",fontSize:12,fontWeight:700,color:"#6b7280",
                borderRight:"1px solid #e5e7eb",background:"#f9fafb",position:"sticky",left:0,zIndex:11}}>
                Dipendente
              </th>
              {giorniFiltrati.map(g=>{
                const ds=toLocalDateStr(g);
                const sdom=isSabDom(g),o2=isOggi(g),isPross=ds>=prossLunediStr;
                const pubbl=isPubblicato(ds);
                return(
                  <th key={ds} style={{padding:"8px 6px",textAlign:"center",fontSize:10,fontWeight:700,
                    background:o2?"#ede9fe":isPross?"#f0fdf4":sdom?"#fff5f5":"#f9fafb",
                    color:o2?"#7c3aed":isPross?"#059669":sdom?"#dc2626":"#374151",
                    borderBottom:`2px solid ${o2?"#c4b5fd":isPross?"#86efac":sdom?"#fca5a5":"#e5e7eb"}`,
                    borderRight:"1px solid #e5e7eb",whiteSpace:"nowrap"}}>
                    {canEdit&&(
                      <input type="checkbox" checked={pubbl} onChange={()=>togglePubblicato(ds)}
                        title={pubbl?"Visibile agli operai — clicca per nascondere questo giorno":"Nascosto agli operai — clicca per pubblicarlo"}
                        style={{marginRight:4,verticalAlign:"middle",cursor:"pointer"}}/>
                    )}
                    {o2&&"📍 "}{fmtG(g).toUpperCase()}
                    {isPross&&<div style={{fontSize:8,color:"#86efac",fontWeight:400}}>sett.pross.</div>}
                    {canEdit&&!pubbl&&<div style={{fontSize:8,color:"#dc2626",fontWeight:700}}>🔒 privato</div>}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {righeVista.length===0?(
              <tr><td colSpan={giorniFiltrati.length+1} style={{padding:48,textAlign:"center",color:"#9ca3af",fontSize:13}}>
                Nessun dipendente
              </td></tr>
            ):righeVista.map((riga,ri)=>{
              const rowBg=ri%2===0?"#fff":"#fafafa";
              return(
                <React.Fragment key={`${riga.uid}_${riga.rowIndex}`}>
                <tr
                  style={{background:rowBg,borderBottom:riga.isLastOfUser&&!vedeMezzi?"2px solid #e5e7eb":"1px solid #f3f4f6"}}>

                  {/* Colonna nome */}
                  <td style={{padding:"4px 10px",fontWeight:700,fontSize:12,color:"#111827",
                    borderRight:"1px solid #e5e7eb",position:"sticky",left:0,
                    background:rowBg,zIndex:2,whiteSpace:"nowrap",userSelect:"none",
                    borderBottom:riga.isLastOfUser&&!vedeMezzi?"2px solid #e5e7eb":"1px solid #f3f4f6"}}>
                    {riga.showName?(
                      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:4}}>
                        <span>{riga.displayName}</span>
                        {canEdit&&!isSettPassata&&(
                          <button onClick={()=>aggiungiRiga(riga.uid)} title="Aggiungi riga"
                            style={{background:"none",border:"none",cursor:"pointer",color:"#c4b5fd",padding:0,flexShrink:0}}
                            onMouseOver={e=>e.currentTarget.style.color="#7c3aed"}
                            onMouseOut={e=>e.currentTarget.style.color="#c4b5fd"}>
                            <Plus size={12}/>
                          </button>
                        )}
                      </div>
                    ):(
                      canEdit&&!isSettPassata&&(
                        <button onClick={()=>eliminaRiga(riga.uid,riga.rowIndex)}
                          style={{background:"none",border:"none",cursor:"pointer",color:"#fca5a5",fontSize:10,fontFamily:"'Inter',sans-serif",padding:0}}
                          onMouseOver={e=>e.currentTarget.style.color="#dc2626"}
                          onMouseOut={e=>e.currentTarget.style.color="#fca5a5"}>
                          elimina riga
                        </button>
                      )
                    )}
                  </td>

                  {/* Celle giorni */}
                  {giorniFiltrati.map((g,ci)=>{
                    const dateStr=toLocalDateStr(g);
                    const cella=getCella(riga.uid,riga.rowIndex,dateStr);
                    const {commessaNome="",commessaId="",ore=""}=cella;
                    const sdom=isSabDom(g),o2=isOggi(g),isPross=dateStr>=prossLunediStr;
                    const isSel=sel?.uid===riga.uid&&sel?.rowIndex===riga.rowIndex&&sel?.dateStr===dateStr;
                    const isTrascOrigine=trascinamento&&trascinamento.ri===ri&&trascinamento.ci===ci;
                    const isTrascSopra=trascinamento&&trascinamento.ri===ri&&trascinaSuCi===ci&&!isTrascOrigine;
                    const campoEvidenziato=(isTrascOrigine||isTrascSopra)?trascinamento.tipo:null;
                    const editabile=canEdit&&!isSettPassata;

                    return(
                      <td key={dateStr}
                        data-ri={ri}
                        data-ci={ci}
                        style={{padding:"5px 4px",
                          borderRight:"1px solid #e5e7eb",
                          borderBottom:riga.isLastOfUser&&!vedeMezzi?"2px solid #e5e7eb":"1px solid #f3f4f6",
                          verticalAlign:"middle",position:"relative",
                          background:(()=>{
                            if(isSel) return "#ede9fe";
                            if(o2) return "#faf5ff";
                            if(isPross) return "#f0fdf4";
                            if(sdom) return "#fff8f8";
                            return "transparent";
                          })(),
                          outline:isSel?"2px solid #7c3aed":"none",
                          outlineOffset:"-2px",
                          cursor:editabile?"pointer":"default"}}
                        onMouseDown={e=>{
                          if(!editabile)return;
                          if(e.target.tagName==="SELECT"||e.target.tagName==="INPUT"||e.target.tagName==="BUTTON")return;
                          setSel({uid:riga.uid,rowIndex:riga.rowIndex,dateStr});
                        }}>

                        {editabile?(
                          <div style={{position:"relative"}}>

                            <CellSelect
                              key={`${riga.uid}_${riga.rowIndex}_${dateStr}`}
                              commessaNome={commessaNome}
                              commessaId={commessaId}
                              ore={ore}
                              commesse={commesse} commessePreesistenti={commessePreesistenti}
                              onCommessaChange={(nome,id)=>updateCella(riga.uid,riga.rowIndex,dateStr,{commessaNome:nome,commessaId:id})}
                              onOreChange={nuoveOre=>updateCella(riga.uid,riga.rowIndex,dateStr,{ore:nuoveOre})}
                              ri={ri} ci={ci} numRighe={righeVista.length} numColonne={giorniFiltrati.length}
                              onTrascinaOreStart={valore=>setTrascinamento({tipo:"ore",ri,ci,dateStr,valore})}
                              onTrascinaNomeStart={valore=>setTrascinamento({tipo:"nome",ri,ci,dateStr,valore,commessaId})}
                              campoEvidenziato={campoEvidenziato}
                              coloreRiempimento={coloriCommesse[commessaId||commessaNome]}
                              onCambiaColore={colore=>salvaColoreCommessa(commessaId||commessaNome,colore)}
                              mostraOre={mostraOre}
                            />
                            {/* ✕ cancella questo giorno — visibile solo se cella selezionata */}
                            {isSel&&(
                              <button
                                data-cell-btn="true"
                                onMouseDown={e=>{e.stopPropagation();e.preventDefault();svuotaCella(riga.uid,riga.rowIndex,dateStr);}}
                                style={{position:"absolute",bottom:-1,right:0,background:"#fee2e2",border:"none",
                                  borderRadius:"3px 0 3px 0",cursor:"pointer",color:"#dc2626",
                                  padding:"1px 5px",fontSize:9,lineHeight:1,zIndex:10}}>
                                ✕
                              </button>
                            )}
                          </div>
                        ):(
                          (()=>{
                            // Chi non può modificare la programmazione non vede nulla in un
                            // giorno non ancora pubblicato — chi la sta scrivendo (canEdit)
                            // vede invece sempre tutto, anche i giorni privati: la spunta
                            // decide chi altro vede, non toglie la vista a chi sta lavorando.
                            const nascostoAOperaio=!canEdit&&!isPubblicato(dateStr);
                            if(nascostoAOperaio)return null;
                            return(
                              <div style={{padding:"3px 5px",fontSize:11,fontWeight:600,color:COLORI[commessaNome]||"#374151",
                                background:coloriCommesse[commessaId||commessaNome]||"transparent",borderRadius:3}}>
                                {commessaNome||""}{ore?<span style={{color:"#7c3aed",fontSize:10,marginLeft:3}}>{ore}h</span>:""}
                                {/* Dove andare: link alla mappa della sede dei lavori della commessa */}
                                {(()=>{ const sd=commesse.find(c=>c.id===commessaId)?.sede; const m=linkMappa(sd?.indirizzo);
                                  return m ? <a href={m} target="_blank" rel="noopener noreferrer" title={descrizioneSede(sd)} style={{marginLeft:4,textDecoration:"none",fontSize:11}}>📍</a> : null; })()}
                              </div>
                            );
                          })()
                        )}
                      </td>
                    );
                  })}
                </tr>
                {vedeMezzi&&(
                  <tr style={{background:"#faf8ff",borderBottom:riga.isLastOfUser?"2px solid #e5e7eb":"1px solid #f3f4f6"}}>
                    <td style={{padding:"2px 10px",fontSize:10.5,fontWeight:600,color:"#6d28d9",borderRight:"1px solid #e5e7eb",
                      position:"sticky",left:0,background:"#faf8ff",zIndex:2,whiteSpace:"nowrap",
                      borderBottom:riga.isLastOfUser?"2px solid #e5e7eb":"1px solid #f3f4f6"}}>
                      🚐 Automezzo
                    </td>
                    {giorniFiltrati.map(g=>{
                      const dateStr=toLocalDateStr(g);
                      const cella=getCella(riga.uid,riga.rowIndex,dateStr);
                      const editabile=canEdit&&!isSettPassata;
                      const conflitto=cella.mezzoId&&conflitti[dateStr]?.has(cella.mezzoId);
                      const avvisoConflitto="Questo mezzo risulta su più cantieri diversi nello stesso giorno";
                      if(!editabile){
                        if(!canEdit&&!isPubblicato(dateStr))return <td key={dateStr} style={{borderRight:"1px solid #e5e7eb"}}/>;
                        return(
                          <td key={dateStr} style={{padding:"2px 6px",fontSize:10.5,color:"#5b21b6",borderRight:"1px solid #e5e7eb",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                            {cella.mezzoId?etichettaMezzo(cella):""}{conflitto&&<span title={avvisoConflitto}> ⚠️</span>}
                          </td>
                        );
                      }
                      const mancante=cella.mezzoId&&!mezzi.some(m=>m.id===cella.mezzoId);
                      return(
                        <td key={dateStr} style={{padding:"2px 4px",borderRight:"1px solid #e5e7eb"}}>
                          <div style={{display:"flex",alignItems:"center",gap:2}}>
                            <select value={cella.mezzoId||""} onChange={e=>assegnaMezzo(riga.uid,riga.rowIndex,dateStr,e.target.value)}
                              title={cella.mezzoAuto?"Assegnato in automatico (stessa squadra/cantiere): si toglie da solo se cambi la commessa":"Mezzo assegnato"}
                              style={{width:"100%",fontSize:10.5,padding:"2px 3px",border:`1px ${cella.mezzoId?"solid":"dashed"} ${conflitto?"#f59e0b":"#ddd6fe"}`,borderRadius:4,
                                background:cella.mezzoId?"#fff":"transparent",color:cella.mezzoId?"#4c1d95":"#a78bfa",fontStyle:cella.mezzoAuto?"italic":"normal",fontFamily:"'Inter',sans-serif"}}>
                              <option value="">— nessuno —</option>
                              {mancante&&<option value={cella.mezzoId}>{etichettaMezzo(cella)} (non più nel Parco mezzi)</option>}
                              {mezzi.map(m=><option key={m.id} value={m.id}>{etichettaMezzo(m)}</option>)}
                            </select>
                            {conflitto&&<span title={avvisoConflitto} style={{fontSize:11}}>⚠️</span>}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Legenda */}
      <div style={{background:"#f9fafb",borderTop:"1px solid #e5e7eb",padding:"6px 20px",flexShrink:0,display:"flex",gap:16,alignItems:"center",flexWrap:"wrap"}}>
        <span style={{fontSize:11,color:"#9ca3af",fontWeight:600}}>LEGENDA:</span>
        {Object.entries(COLORI).map(([l,col])=>(
          <span key={l} style={{display:"flex",alignItems:"center",gap:4,fontSize:11,color:"#6b7280"}}>
            <span style={{width:10,height:10,borderRadius:2,background:col+"40",border:`1px solid ${col}`,display:"inline-block"}}/>
            {l}
          </span>
        ))}
      </div>
    </div>
  );
}
